import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, ExternalLink, AlertTriangle, Check } from 'lucide-react';
import { get, post } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Navbar } from '@/widgets/Navbar';
import { Button, Card, Modal, Badge } from '@/shared/ui';
import { formatDate } from '@/shared/utils/format';
import type { Family } from '@/entities/family';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Plan definitions (matches backend PLAN_INFO)
// ---------------------------------------------------------------------------
const PLANS = [
  {
    type: 'intro' as const,
    name: 'Intro Plan',
    price: '$4.99',
    period: '/month',
    description: 'Perfect for getting started.',
    features: ['Up to 8 family members', 'Chores & rewards', 'Point ledger', 'App notifications'],
    badge: null,
  },
  {
    type: 'standard' as const,
    name: 'Standard Plan',
    price: '$7.99',
    period: '/month',
    description: 'Full access, all features.',
    features: ['Up to 8 family members', 'Chores & rewards', 'Point ledger', 'App notifications', 'Priority support'],
    badge: 'Best Value',
  },
] as const;

type PlanType = (typeof PLANS)[number]['type'];

// ---------------------------------------------------------------------------
// Response type (matches enriched backend response)
// ---------------------------------------------------------------------------
interface SubscriptionStatusResponse {
  status: Family['subscriptionStatus'];
  planType: PlanType | null;
  planName: string;
  priceLabel: string | null;
  nextBillingDate: string | null;
  trialEndsAt: string | null;
  subscriptionEndsAt: string | null;
  paypalSubscriptionId: string | null;
}

interface CheckoutResponse {
  approvalUrl: string;
  subscriptionId: string;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
function SubscriptionStatusBadge({
  status,
}: {
  status: Family['subscriptionStatus'];
}): React.ReactElement {
  const config: Record<Family['subscriptionStatus'], { variant: 'success' | 'info' | 'danger' | 'neutral'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    trialing: { variant: 'info', label: 'Free Trial' },
    suspended: { variant: 'danger', label: 'Suspended' },
    cancelled: { variant: 'neutral', label: 'Cancelled' },
  };
  const { variant, label } = config[status];
  return <Badge variant={variant}>{label}</Badge>;
}

// ---------------------------------------------------------------------------
// Plan selector modal
// ---------------------------------------------------------------------------
function PlanSelectModal({
  isOpen,
  onClose,
  onSelect,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (planType: PlanType) => void;
  isLoading: boolean;
}): React.ReactElement {
  const [selected, setSelected] = useState<PlanType>('standard');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose a Plan" size="lg">
      <div className="flex flex-col gap-5">
        <p className="text-sm text-slate-600">
          All plans include a 14-day free trial. Cancel anytime.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          {PLANS.map((plan) => (
            <button
              key={plan.type}
              type="button"
              aria-pressed={selected === plan.type}
              onClick={() => setSelected(plan.type)}
              className={cn(
                'relative flex flex-col gap-3 rounded-xl border-2 p-4 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                selected === plan.type
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-slate-200 bg-white hover:border-slate-300',
              )}
            >
              {plan.badge && (
                <span className="absolute right-3 top-3 rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
                  {plan.badge}
                </span>
              )}
              <div>
                <p className="text-sm font-semibold text-slate-900">{plan.name}</p>
                <p className="mt-0.5 text-xs text-slate-500">{plan.description}</p>
              </div>
              <div className="flex items-baseline gap-0.5">
                <span className="text-2xl font-bold text-slate-900">{plan.price}</span>
                <span className="text-sm text-slate-500">{plan.period}</span>
              </div>
              <ul className="flex flex-col gap-1.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Check size={12} className="shrink-0 text-secondary-600" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              {selected === plan.type && (
                <div className="absolute right-3 bottom-3 h-4 w-4 rounded-full bg-primary-600 flex items-center justify-center">
                  <Check size={10} className="text-white" aria-hidden="true" />
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            fullWidth
            isLoading={isLoading}
            onClick={() => onSelect(selected)}
          >
            Continue with {PLANS.find((p) => p.type === selected)?.name}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// BillingPage
// ---------------------------------------------------------------------------
export default function BillingPage(): React.ReactElement {
  const { appUser } = useAuth();
  const queryClient = useQueryClient();
  const familyId = appUser?.familyId ?? '';

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPlanSelect, setShowPlanSelect] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const { data: subData, isLoading } = useQuery<SubscriptionStatusResponse>({
    queryKey: ['subscription', familyId],
    queryFn: () => get<SubscriptionStatusResponse>('/subscriptions/status'),
    enabled: Boolean(familyId),
  });

  // Redirect to PayPal approval page for the selected plan
  const handleStartCheckout = async (planType: PlanType) => {
    setCheckoutLoading(true);
    try {
      const res = await post<CheckoutResponse>('/subscriptions/create', { planType });
      window.location.href = res.approvalUrl;
    } catch {
      setCheckoutLoading(false);
      setShowPlanSelect(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      await post<void>('/subscriptions/cancel');
      setShowCancelConfirm(false);
      void queryClient.invalidateQueries({ queryKey: ['subscription', familyId] });
    } finally {
      setCancelLoading(false);
    }
  };

  const isSuspended = subData?.status === 'suspended';
  const isCancelled = subData?.status === 'cancelled';
  const isActive = subData?.status === 'active';
  const isTrialing = subData?.status === 'trialing';
  const needsSubscription = isTrialing || isCancelled || isSuspended;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6 space-y-6">
        <div className="flex items-center gap-2">
          <CreditCard size={22} className="text-primary-600" aria-hidden="true" />
          <h1 className="text-xl font-bold text-slate-900">Billing</h1>
        </div>

        {/* Suspended banner */}
        {isSuspended && (
          <div className="flex items-start gap-3 rounded-xl bg-danger-50 border border-danger-200 p-4">
            <AlertTriangle size={20} className="text-danger-600 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-danger-800">Subscription suspended</p>
              <p className="text-xs text-danger-700 mt-0.5">
                A payment failed. Update your payment method on PayPal, or start a new subscription below.
              </p>
            </div>
          </div>
        )}

        {/* Current plan card */}
        <Card>
          <h2 className="mb-4 text-base font-semibold text-slate-800">Current Plan</h2>

          {isLoading ? (
            <div className="space-y-3 animate-pulse">
              <div className="h-6 w-32 rounded bg-slate-100" />
              <div className="h-4 w-48 rounded bg-slate-100" />
              <div className="h-4 w-36 rounded bg-slate-100" />
            </div>
          ) : subData ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-lg font-bold text-slate-900">{subData.planName}</span>
                <SubscriptionStatusBadge status={subData.status} />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {subData.priceLabel && (
                  <div className="rounded-lg bg-slate-50 px-4 py-3">
                    <p className="text-xs text-slate-500 mb-1">Price</p>
                    <p className="text-sm font-semibold text-slate-800">{subData.priceLabel}</p>
                  </div>
                )}

                {isTrialing && subData.trialEndsAt && (
                  <div className="rounded-lg bg-primary-50 px-4 py-3">
                    <p className="text-xs text-primary-600 mb-1">Trial ends</p>
                    <p className="text-sm font-semibold text-primary-800">
                      {formatDate(subData.trialEndsAt)}
                    </p>
                  </div>
                )}

                {isActive && subData.nextBillingDate && (
                  <div className="rounded-lg bg-slate-50 px-4 py-3">
                    <p className="text-xs text-slate-500 mb-1">Next billing date</p>
                    <p className="text-sm font-semibold text-slate-800">
                      {formatDate(subData.nextBillingDate)}
                    </p>
                  </div>
                )}

                {isCancelled && subData.subscriptionEndsAt && (
                  <div className="rounded-lg bg-warning-50 px-4 py-3">
                    <p className="text-xs text-warning-700 mb-1">Access until</p>
                    <p className="text-sm font-semibold text-warning-800">
                      {formatDate(subData.subscriptionEndsAt)}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 pt-2">
                {needsSubscription && (
                  <Button
                    variant="primary"
                    onClick={() => setShowPlanSelect(true)}
                  >
                    {isCancelled ? 'Resubscribe' : 'Set up billing'}
                  </Button>
                )}

                {isSuspended && (
                  <Button
                    variant="ghost"
                    leftIcon={<ExternalLink size={14} />}
                    onClick={() => window.open('https://www.paypal.com/myaccount/autopay/', '_blank', 'noopener,noreferrer')}
                  >
                    Update payment on PayPal
                  </Button>
                )}

                {isActive && (
                  <Button
                    variant="ghost"
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-danger-600 hover:bg-danger-50"
                  >
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Unable to load subscription info.</p>
          )}
        </Card>

        {/* PayPal branding */}
        <Card className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100"
            aria-hidden="true"
          >
            <span className="text-lg font-bold text-blue-700">P</span>
          </div>
          <p className="text-sm text-slate-600">
            Billing is managed securely by{' '}
            <span className="font-semibold text-blue-700">PayPal</span>.
            Your payment information is never stored on our servers.
          </p>
        </Card>
      </main>

      {/* Plan selection modal */}
      <PlanSelectModal
        isOpen={showPlanSelect}
        onClose={() => setShowPlanSelect(false)}
        onSelect={(planType) => void handleStartCheckout(planType)}
        isLoading={checkoutLoading}
      />

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title="Cancel Subscription"
        size="sm"
      >
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg bg-warning-50 p-3">
            <AlertTriangle size={18} className="text-warning-600 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-warning-800">
              Your subscription will remain active until the end of the current billing period.
              After that, you'll lose access to KidKred features.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="danger"
              isLoading={cancelLoading}
              onClick={() => void handleCancelSubscription()}
              fullWidth
            >
              Yes, cancel subscription
            </Button>
            <Button variant="ghost" onClick={() => setShowCancelConfirm(false)} fullWidth>
              Keep subscription
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
